import React, { useEffect, useRef, useState, useCallback } from 'react';

const Pong = () => {
  // Constants
  const BASE_SPEED = 2.5;
  const SPEED_INCREASE = 0.75;
  
  // States
  const [configInput, setConfigInput] = useState(() => {
    const saved = localStorage.getItem('pongGameConfig');
    return saved ? JSON.parse(saved) : { rounds: 3, pointsPerRound: 5 };
  });
  const [gameConfig, setGameConfig] = useState({ rounds: 3, pointsPerRound: 5 });
  const [currentRound, setCurrentRound] = useState(1);
  const [scoreA, setScoreA] = useState(0);
  const [scoreB, setScoreB] = useState(0);
  const [paddleA, setPaddleA] = useState(250);
  const [paddleB, setPaddleB] = useState(250);
  const [roundWins, setRoundWins] = useState({ A: 0, B: 0 });
  const [gameState, setGameState] = useState('config');
  const [winner, setWinner] = useState(null);
  const [isFocused, setIsFocused] = useState(false);
  const [speedMultiplier, setSpeedMultiplier] = useState(1);
  
  // Refs - all in one place
  const ballRef = useRef({ x: 400, y: 300, dx: 0, dy: 0, speed: BASE_SPEED });
  const canvasRef = useRef(null);
  const canScore = useRef(true);
  const paddleAPos = useRef(paddleA);
  const paddleBPos = useRef(paddleB);
  const animationFrameId = useRef(null);
  const gameEndedRef = useRef(false);
  const previousGameState = useRef('config');

  // Refs
  const flameTime = useRef(0);
  const emberParticles = useRef([]);
  const lastPosition = useRef({ x: 400, y: 300 });
  const velocity = useRef({ x: 0, y: 0 });

  // Add ref to track if game has started
  const gameStarted = useRef(false);

  // Add ref to track initial serve
  const initialServe = useRef(true);

  // Add ref to track if ball has started moving
  const ballMoving = useRef(false);

  // Add speed multiplier ref
  const speedMultiplierRef = useRef(1);

  // Update paddle color helper with more unique colors
  const getPaddleColor = useCallback(() => {
    switch(currentRound) {
      case 1: return '#FFFFFF'; // White
      case 2: return '#00FF00'; // Green
      case 3: return '#FF00FF'; // Magenta
      case 4: return '#00FFFF'; // Cyan
      case 5: return '#FFD700'; // Gold
      case 6: return '#FF4500'; // OrangeRed
      case 7: return '#9400D3'; // DarkViolet
      case 8: return '#00FA9A'; // MediumSpringGreen
      case 9: return '#FF69B4'; // HotPink
      default: return '#FF0000'; // Red for final/extra rounds
    }
  }, [currentRound]);

  // Add particle system for final round
  const particles = useRef([]);
  const createParticle = useCallback((x, y, angle) => {
    return {
      x,
      y,
      dx: Math.cos(angle) * (Math.random() * 2 + 2),
      dy: Math.sin(angle) * (Math.random() * 2 + 2),
      life: 1.0
    };
  }, []);

  // Update paddle position refs when state changes
  useEffect(() => {
    paddleAPos.current = paddleA;
  }, [paddleA]);

  useEffect(() => {
    paddleBPos.current = paddleB;
  }, [paddleB]);

  // Config input handler
  const handleConfigChange = (e, field) => {
    const value = e.target.value;
    setConfigInput(prev => ({
      ...prev,
      [field]: value === '' ? '' : parseInt(value) || 1
    }));
  };

  // Update resetBall to use ref instead of state
  const resetBall = useCallback((scoringPlayer = null) => {
    const angle = (Math.random() * Math.PI/2) - Math.PI/4;
    let direction = 1;
    let startX = 400;

    if (scoringPlayer === 'B') {
      direction = -1;
      startX = 600;
    } else if (scoringPlayer === 'A') {
      direction = 1;
      startX = 200;
    } else {
      direction = Math.random() < 0.5 ? 1 : -1;
      startX = 400;
    }
    
    const exactSpeed = BASE_SPEED * speedMultiplierRef.current;
    
    ballRef.current = {
      x: startX,
      y: 300,
      dx: Math.cos(angle) * exactSpeed * direction,
      dy: Math.sin(angle) * exactSpeed,
      speed: exactSpeed
    };
    
    ballMoving.current = true;
    canScore.current = true;
  }, []); // Empty dependencies since using ref

  // Update handleScore to maintain serve state
  const handleScore = useCallback((player) => {
    if (!canScore.current || gameEndedRef.current) return;
    canScore.current = false;
    initialServe.current = false;  // Game has had a score

    if (player === 'A') {
      setScoreA(prev => prev + 1);
    } else {
      setScoreB(prev => prev + 1);
    }
    
    resetBall(player);
  }, [resetBall]);

  // Update round completion to handle speed changes
  const handleRoundEnd = useCallback((winner) => {
    setRoundWins(prev => ({
      ...prev,
      [winner]: prev[winner] + 1
    }));

    setCurrentRound(prevRound => {
      const newRound = prevRound + 1;
      
      if (newRound > gameConfig.rounds) {
        setGameState('finished');
        setWinner(roundWins.A + (winner === 'A' ? 1 : 0) > roundWins.B + (winner === 'B' ? 1 : 0) ? 'A' : 'B');
        gameEndedRef.current = true;
        return prevRound;
      }

      const newSpeed = 1 + (SPEED_INCREASE * (newRound - 1));
      speedMultiplierRef.current = newSpeed;
      setSpeedMultiplier(newSpeed);
      setScoreA(0);
      setScoreB(0);
      resetBall();
      
      return newRound;
    });
  }, [gameConfig.rounds, roundWins.A, roundWins.B, resetBall]);

  // Update draw function to reposition score labels
  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const ball = ballRef.current;

    // Clear canvas
    ctx.fillStyle = '#000000';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Draw center line
    ctx.strokeStyle = getPaddleColor();
    ctx.setLineDash([5, 15]);
    ctx.beginPath();
    ctx.moveTo(400, 0);
    ctx.lineTo(400, 600);
    ctx.stroke();
    ctx.setLineDash([]);

    // Draw ball with effects
    if (currentRound === gameConfig.rounds) {
      // Final round fireball effect
      // Draw flame trail
      ctx.save();
      const gradient = ctx.createRadialGradient(ball.x, ball.y, 0, ball.x, ball.y, 30);
      gradient.addColorStop(0, 'rgba(255, 255, 0, 0.8)');
      gradient.addColorStop(0.4, 'rgba(255, 120, 0, 0.4)');
      gradient.addColorStop(1, 'rgba(255, 0, 0, 0)');
      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.arc(ball.x, ball.y, 30, 0, Math.PI * 2);
      ctx.fill();

      // Create new particles
      if (Math.random() < 0.3) {
        const angle = Math.random() * Math.PI * 2;
        particles.current.push(createParticle(ball.x, ball.y, angle));
      }

      // Update and draw particles
      particles.current = particles.current.filter(p => {
        p.x += p.dx;
        p.y += p.dy;
        p.life -= 0.02;
        
        if (p.life > 0) {
          ctx.fillStyle = `rgba(255, ${Math.floor(Math.random() * 128)}, 0, ${p.life})`;
          ctx.beginPath();
          ctx.arc(p.x, p.y, 2, 0, Math.PI * 2);
          ctx.fill();
          return true;
        }
        return false;
      });

      ctx.restore();
    }

    // Draw ball
    ctx.fillStyle = getPaddleColor();
    ctx.beginPath();
    ctx.arc(ball.x, ball.y, 10, 0, Math.PI * 2);
    ctx.fill();

    // Draw paddles with round colors
    ctx.fillStyle = getPaddleColor();
    ctx.fillRect(30, paddleAPos.current, 10, 100);
    ctx.fillRect(760, paddleBPos.current, 10, 100);

    // Draw scores
    ctx.font = '40px Arial';
    ctx.fillStyle = '#FFFFFF';
    ctx.textAlign = 'center';
    ctx.fillText(scoreA.toString(), 200, 80);
    ctx.fillText(scoreB.toString(), 600, 80);

    // Draw round wins below scores
    ctx.font = '20px Arial';
    ctx.fillStyle = '#888888';
    ctx.fillText(`Rounds: ${roundWins.A}`, 200, 110);
    ctx.fillText(`Rounds: ${roundWins.B}`, 600, 110);

    // Draw round info
    ctx.font = '20px Arial';
    ctx.fillStyle = '#FFFFFF';
    ctx.fillText(`Round ${currentRound}/${gameConfig.rounds}`, 400, 30);

    // Draw points to win
    ctx.font = '16px Arial';
    ctx.fillText(`Points to win: ${gameConfig.pointsPerRound}`, 400, 60);

    // Draw game state messages
    if (gameState === 'finished') {
    }

    if (gameState === 'paused') {
      ctx.font = '40px Arial';
      ctx.fillText('Paused', 400, 300);
    }

    if (!isFocused) {
      ctx.font = '20px Arial';
      ctx.fillText('Click to focus', 400, 350);
    }
  }, [gameState, currentRound, gameConfig.rounds, gameConfig.pointsPerRound, scoreA, scoreB, roundWins, winner, isFocused, speedMultiplier, getPaddleColor, createParticle]);

  // Define updateGame function
  const updateGame = useCallback(() => {
    if (gameState === 'playing') {
      const ball = ballRef.current;
      const exactSpeed = ball.speed;

      // Wall collisions - simple reflection
      if (ball.y <= 0 || ball.y >= 590) {
        ball.dy = -ball.dy;
      }

      // Paddle collisions with exact speed
      if (canScore.current) {
        // Paddle A
        if (ball.x <= 40 && ball.x >= 30 && 
            ball.y >= paddleAPos.current && 
            ball.y <= paddleAPos.current + 100) {
          const relativeIntersectY = (paddleAPos.current + 50) - ball.y;
          const normalizedIntersectY = relativeIntersectY / 50;
          const bounceAngle = normalizedIntersectY * Math.PI/3;
          
          ball.dx = Math.cos(bounceAngle) * exactSpeed;
          ball.dy = -Math.sin(bounceAngle) * exactSpeed;
        }
        
        // Paddle B
        if (ball.x >= 760 && ball.x <= 770 && 
            ball.y >= paddleBPos.current && 
            ball.y <= paddleBPos.current + 100) {
          const relativeIntersectY = (paddleBPos.current + 50) - ball.y;
          const normalizedIntersectY = relativeIntersectY / 50;
          const bounceAngle = normalizedIntersectY * Math.PI/3;
          
          ball.dx = -Math.cos(bounceAngle) * exactSpeed;
          ball.dy = -Math.sin(bounceAngle) * exactSpeed;
        }
      }

      // Check for scoring
      if (ball.x <= 0) {
        handleScore('B');
        return;
      } else if (ball.x >= 800) {
        handleScore('A');
        return;
      }

      // Update position
      ball.x += ball.dx;
      ball.y += ball.dy;
    }
  }, [gameState, handleScore]);

  // Update game loop to handle initial serve
  useEffect(() => {
    const gameLoop = () => {
      if (gameState === 'playing') {
        if (initialServe.current) {
          initialServe.current = false;
          resetBall();
        }
        updateGame();
      }
      draw();
      animationFrameId.current = requestAnimationFrame(gameLoop);
    };

    if (canvasRef.current) {
      gameLoop();
    }

    return () => {
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
      }
    };
  }, [gameState, draw, updateGame, resetBall]);

  // Game control functions
  const startGame = () => {
    const validConfig = {
      rounds: Math.max(1, parseInt(configInput.rounds) || 1),
      pointsPerRound: Math.max(1, parseInt(configInput.pointsPerRound) || 1)
    };
    
    setConfigInput(validConfig);
    setGameConfig(validConfig);
    localStorage.setItem('pongGameConfig', JSON.stringify(validConfig));
    setGameState('playing');
    setScoreA(0);
    setScoreB(0);
    setCurrentRound(1);
    setRoundWins({ A: 0, B: 0 });
    setWinner(null);
    ballRef.current = {
      x: 400,
      y: 300,
      dx: 10.0,
      dy: 10.0
    };
  };

  const pauseGame = () => {
    setGameState('paused');
  };

  const resumeGame = () => {
    setGameState('playing');
  };

  // Update startNewGame
  const startNewGame = useCallback(() => {
    setGameState('config');
    setCurrentRound(1);
    setScoreA(0);
    setScoreB(0);
    setRoundWins({ A: 0, B: 0 });
    setWinner(null);
    setSpeedMultiplier(1);
    ballMoving.current = false;
    gameEndedRef.current = false;
  }, []);

  // Update handleCanvasClick
  const handleCanvasClick = useCallback((e) => {
    const canvas = canvasRef.current;
    if (canvas) {
      canvas.focus();
      setIsFocused(true);
      if (gameState === 'config') {
        setGameState('playing');
        resetBall();
      }
    }
  }, [gameState, resetBall]);

  // Update focus handling to never reset ball
  const handleFocusChange = useCallback((focused) => {
    setIsFocused(focused);
    if (!focused && gameState === 'playing') {
      setGameState('paused');
      previousGameState.current = 'playing';
    } else if (focused && gameState === 'paused' && previousGameState.current === 'playing') {
      setGameState('playing');
    }
  }, [gameState]);

  // Ensure keyboard events are bound to window
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isFocused) return;

      // Prevent default browser behavior for game controls
      if (['w', 's', 'arrowup', 'arrowdown', ' '].includes(e.key.toLowerCase())) {
        e.preventDefault();
      }

      switch (e.key.toLowerCase()) {
        case 'w':
          setPaddleA(prev => Math.max(prev - 20, 0));
          paddleAPos.current = Math.max(paddleAPos.current - 20, 0);
          break;
        case 's':
          setPaddleA(prev => Math.min(prev + 20, 500));
          paddleAPos.current = Math.min(paddleAPos.current + 20, 500);
          break;
        case 'arrowup':
          setPaddleB(prev => Math.max(prev - 20, 0));
          paddleBPos.current = Math.max(paddleBPos.current - 20, 0);
          break;
        case 'arrowdown':
          setPaddleB(prev => Math.min(prev + 20, 500));
          paddleBPos.current = Math.min(paddleBPos.current + 20, 500);
          break;
        case ' ':
          if (gameState === 'paused') {
            setGameState('playing');
          } else if (gameState === 'playing') {
            setGameState('paused');
          }
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isFocused, gameState]);

  // Add effect to handle game start
  useEffect(() => {
    if (gameState === 'playing' && currentRound === 1 && scoreA === 0 && scoreB === 0) {
      // Reset speed at the start of a new game
      setSpeedMultiplier(1);
      //resetBall();
    }
  }, [gameState, currentRound, scoreA, scoreB]);

  // Add canvas styling
  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      canvas.style.boxShadow = isFocused ? '0 0 20px 5px rgba(0, 102, 204, 0.7)' : 'none';
      canvas.style.transition = 'box-shadow 0.3s ease';
    }
  }, [isFocused]);

  // Initialize ball ref with starting values
  useEffect(() => {
    if (!ballRef.current) {
      ballRef.current = {
        x: 400,
        y: 300,
        dx: 0,
        dy: 0,
        speed: BASE_SPEED
      };
    }
  }, []);

  // Update score monitoring effect
  useEffect(() => {
    const checkRoundEnd = () => {
      if (scoreA >= gameConfig.pointsPerRound) {
        handleRoundEnd('A');
      } else if (scoreB >= gameConfig.pointsPerRound) {
        handleRoundEnd('B');
      }
    };

    if (gameState === 'playing') {
      checkRoundEnd();
    }
  }, [scoreA, scoreB, gameState, gameConfig.pointsPerRound, handleRoundEnd]);

  // Return canvas with game-end UI
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        margin: 0,
        padding: 0,
        backgroundColor: 'transparent',
        position: 'relative'
      }}
    >
      <canvas
        ref={canvasRef}
        width={800}
        height={600}
        onClick={handleCanvasClick}
        onBlur={() => handleFocusChange(false)}
        onFocus={() => handleFocusChange(true)}
        onKeyDown={(e) => e.preventDefault()}
        tabIndex={0}
        style={{
          display: 'block',
          boxShadow: isFocused 
            ? '0 0 20px 5px rgba(0, 102, 204, 0.7)' 
            : 'none',
          transition: 'all 0.3s ease',
          border: '1px solid #333',
          backgroundColor: '#000000',
          outline: 'none',
          cursor: 'pointer'
        }}
      />
      {gameState === 'config' && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          textAlign: 'center',
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          padding: '20px',
          borderRadius: '10px',
          border: '1px solid #666'
        }}>
          <div style={{ marginBottom: '15px' }}>
            <label style={{ 
              color: 'white', 
              display: 'block', 
              marginBottom: '5px',
              fontSize: '16px'
            }}>
              Rounds (1-9)
            </label>
            <input
              type="number"
              min="1"
              max="9"
              value={configInput.rounds}
              onChange={(e) => setConfigInput(prev => ({
                ...prev,
                rounds: Math.min(9, Math.max(1, parseInt(e.target.value) || 1))
              }))}
              style={{ 
                width: '100px',
                padding: '5px',
                fontSize: '16px',
                textAlign: 'center'
              }}
            />
          </div>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ 
              color: 'white', 
              display: 'block', 
              marginBottom: '5px',
              fontSize: '16px'
            }}>
              Points per Round (1-99)
            </label>
            <input
              type="number"
              min="1"
              max="99"
              value={configInput.pointsPerRound}
              onChange={(e) => setConfigInput(prev => ({
                ...prev,
                pointsPerRound: Math.min(99, Math.max(1, parseInt(e.target.value) || 1))
              }))}
              style={{ 
                width: '100px',
                padding: '5px',
                fontSize: '16px',
                textAlign: 'center'
              }}
            />
          </div>
          <button 
            onClick={() => {
              setGameConfig(configInput);
              setGameState('playing');
              const canvas = canvasRef.current;
              if (canvas) {
                canvas.focus();
                setIsFocused(true);
              }
            }}
            style={{
              padding: '8px 24px',
              fontSize: '16px',
              cursor: 'pointer',
              backgroundColor: '#4CAF50',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              transition: 'background-color 0.3s'
            }}
            onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#45a049'}
            onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#4CAF50'}
          >
            Start Game
          </button>
        </div>
      )}
      {gameState === 'finished' && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          textAlign: 'center',
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          padding: '20px',
          borderRadius: '10px',
          border: '1px solid #666'
        }}>
          <h2 style={{ 
            color: 'white', 
            marginBottom: '20px',
            fontSize: '32px'
          }}>
            Player {winner} Wins!
          </h2>
          <button 
            onClick={() => {
              startNewGame();
              const canvas = canvasRef.current;
              if (canvas) {
                canvas.focus();
                setIsFocused(true);
              }
            }}
            style={{
              padding: '8px 24px',
              fontSize: '16px',
              cursor: 'pointer',
              backgroundColor: '#4CAF50',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              transition: 'background-color 0.3s'
            }}
            onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#45a049'}
            onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#4CAF50'}
          >
            Play Again
          </button>
        </div>
      )}
    </div>
  );
};

export default Pong; 